import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DescriptionIcon from "@material-ui/icons/Description";
import GetAppIcon from "@material-ui/icons/GetApp";
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as XLSX from "xlsx";

const useStyles = makeStyles((theme) => ({
  card: {
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  header: {
    padding: theme.spacing(2, 2.5),
    backgroundColor: "#fafafa",
  },
  content: {
    padding: theme.spacing(3),
  },
  dropZone: {
    border: "2px dashed #cfd4dc",
    borderRadius: 8,
    padding: theme.spacing(5),
    textAlign: "center",
    background: "#fcfcfd",
    cursor: "pointer",
  },
  fileName: {
    marginTop: theme.spacing(2),
    fontWeight: 600,
  },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  primary: {
    textTransform: "none",
    fontWeight: 600,
  },
  secondary: {
    textTransform: "none",
  },
  auctionBox: {
    marginBottom: theme.spacing(3),
  },
  helper: {
    fontSize: 12,
    color: "#777",
    marginTop: theme.spacing(0.75),
  },
}));

const normalize = (value) =>
  String(value === undefined || value === null ? "" : value).trim();

const parseBoolean = (value) => {
  const v = normalize(value).toLowerCase();
  return ["yes", "y", "true", "1", "include", "included"].includes(v);
};

const findHeader = (headers, names) => {
  const normalized = headers.map((h) => normalize(h).toLowerCase());

  for (const name of names) {
    const index = normalized.indexOf(name.toLowerCase());

    if (index !== -1) {
      return headers[index];
    }
  }

  return null;
};

const rowsFromSheet = (sheet) =>
  XLSX.utils.sheet_to_json(sheet, {
    defval: "",
    raw: false,
  });

const buildInvoiceRows = (workbook, selectedAuction) => {
  const invoicesSheet =
    workbook.Sheets["Invoices"] ||
    workbook.Sheets[workbook.SheetNames[0]];

  const lotsSheet = workbook.Sheets["Lots"];
  const addonsSheet = workbook.Sheets["Addons"];

  if (!invoicesSheet) {
    throw new Error("Invoices sheet was not found.");
  }

  const invoiceSource = rowsFromSheet(invoicesSheet);
  const lotSource = lotsSheet ? rowsFromSheet(lotsSheet) : [];
  const addonSource = addonsSheet ? rowsFromSheet(addonsSheet) : [];

  const invoiceMap = {};
  const result = [];

  invoiceSource.forEach((source, index) => {
    const headers = Object.keys(source);

    const groupKey =
      findHeader(headers, [
        "Invoice Group",
        "invoice_group",
        "Group",
      ]) || headers[0];

    const customerKey = findHeader(headers, [
      "Customer Name",
      "custom_name",
      "customer_name",
    ]);

    const emailKey = findHeader(headers, [
      "Email",
      "Email Address",
      "custom_email",
      "customer_email",
    ]);

    const imageKey = findHeader(headers, [
      "Include Artwork",
      "Include Image",
      "withImage",
      "with_image",
    ]);

    const group =
      normalize(source[groupKey]) || `ROW-${index + 2}`;

    const row = {
      row_number: index + 2,
      invoice_group: group,

      customer_name: customerKey
        ? normalize(source[customerKey])
        : "",

      email: emailKey
        ? normalize(source[emailKey])
        : "",

      // IMPORTANT:
      // Auction/event comes from the selector in Step 1,
      // NOT from Excel.
      event_id: selectedAuction ? selectedAuction.id : "",
      event_name: selectedAuction
        ? selectedAuction.auction_name
        : "",

      include_artwork: imageKey
        ? parseBoolean(source[imageKey])
        : true,

      lot_numbers: [],
      addons: [],
      status: "VALID",
      validation_error: "",
    };

    invoiceMap[group] = row;
    result.push(row);
  });

  lotSource.forEach((source) => {
    const headers = Object.keys(source);

    const groupKey = findHeader(headers, [
      "Invoice Group",
      "invoice_group",
      "Group",
    ]);

    const lotKey = findHeader(headers, [
      "Lot Number",
      "lot_number",
      "Lot",
    ]);

    if (!groupKey || !lotKey) {
      return;
    }

    const group = normalize(source[groupKey]);
    const lot = normalize(source[lotKey]);

    if (group && lot && invoiceMap[group]) {
      invoiceMap[group].lot_numbers.push(lot);
    }
  });

  addonSource.forEach((source) => {
    const headers = Object.keys(source);

    const groupKey = findHeader(headers, [
      "Invoice Group",
      "invoice_group",
      "Group",
    ]);

    const descriptionKey = findHeader(headers, [
      "Description",
      "description",
    ]);

    const priceKey = findHeader(headers, [
      "Price",
      "price",
      "Amount",
      "amount",
    ]);

    if (!groupKey || !descriptionKey || !priceKey) {
      return;
    }

    const group = normalize(source[groupKey]);
    const description = normalize(source[descriptionKey]);
    const price = Number(source[priceKey] || 0);

    if (
      group &&
      invoiceMap[group] &&
      description
    ) {
      invoiceMap[group].addons.push({
        discription: description,
        price: Number.isFinite(price) ? price : 0,
      });
    }
  });

  result.forEach((row) => {
    const errors = [];

    if (!selectedAuction) {
      errors.push("Auction is required.");
    }

    if (!row.customer_name) {
      errors.push("Customer Name is required.");
    }

    if (!row.email) {
      errors.push("Email is required.");
    }

    if (!row.lot_numbers.length) {
      errors.push("At least one Lot Number is required.");
    }

    if (errors.length) {
      row.status = "ERROR";
      row.validation_error = errors.join(" ");
    }
  });

  return result;
};

const createTemplate = () => {
  const wb = XLSX.utils.book_new();

  // NO auction/event column here.
  // Auction is selected in Step 1 of the UI.
  const invoices = [
    [
      "Invoice Group",
      "Customer Name",
      "Email",
      "Include Artwork",
    ],
    [
      "INV001",
      "Juan Cruz",
      "juan@gmail.com",
      "YES",
    ],
    [
      "INV002",
      "Pedro Cruz",
      "pedro@gmail.com",
      "NO",
    ],
  ];

  const lots = [
    ["Invoice Group", "Lot Number"],
    ["INV001", "1001"],
    ["INV001", "1002"],
    ["INV001", "1003"],
    ["INV002", "1005"],
  ];

  const addons = [
    ["Invoice Group", "Description", "Price"],
    ["INV001", "Shipping", "500"],
    ["INV001", "Handling", "200"],
    ["INV002", "Shipping", "300"],
  ];

  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(invoices),
    "Invoices"
  );

  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(lots),
    "Lots"
  );

  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(addons),
    "Addons"
  );

  XLSX.writeFile(
    wb,
    "invoice_bulk_template.xlsx"
  );
};

const BulkInvoiceUpload = ({
  file,
  auctions,
  selectedAuction,
  onAuctionChange,
  onBack,
  onParsed,
}) => {
  const classes = useStyles();
  const inputRef = useRef(null);

  const [error, setError] = useState("");

  const handleFile = async (selectedFile) => {
    setError("");

    if (!selectedAuction) {
      setError("Please select an auction first.");
      return;
    }

    if (!selectedFile) {
      return;
    }

    const validExtension =
      /\.(xlsx|xls)$/i.test(selectedFile.name);

    if (!validExtension) {
      setError(
        "Please select an Excel file (.xlsx or .xls)."
      );
      return;
    }

    try {
      const buffer = await selectedFile.arrayBuffer();

      const workbook = XLSX.read(buffer, {
        type: "array",
      });

      const rows = buildInvoiceRows(
        workbook,
        selectedAuction
      );

      const valid = rows.filter(
        (row) => row.status === "VALID"
      ).length;

      const errors = rows.length - valid;

      onParsed(
        selectedFile,
        rows,
        {
          total: rows.length,
          valid,
          errors,
        },
        selectedAuction
      );
    } catch (err) {
      console.error(err);

      setError(
        "Unable to read the Excel file. Please check the template."
      );
    }
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.header}
        avatar={<DescriptionIcon color="primary" />}
        title={
          <Typography variant="h6">
            Bulk Invoice Upload
          </Typography>
        }
        subheader="STEP 1 — Select auction and upload your Excel file"
      />

      <Divider />

      <CardContent className={classes.content}>
        {/* =========================
            AUCTION SELECTOR
        ========================= */}

        <Box className={classes.auctionBox}>
          <Autocomplete
            value={selectedAuction}
            options={auctions || []}
            getOptionLabel={(option) =>
              option ? option.auction_name || "" : ""
            }
            onChange={(event, value) => {
              setError("");
              onAuctionChange(value);
            }}
            isOptionEqualToValue={(option, value) =>
              option && value
                ? option.id === value.id
                : option === value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Auction"
                variant="outlined"
                fullWidth
              />
            )}
          />

          <Typography className={classes.helper}>
            This auction will be used for every invoice in this
            Excel batch. It will be saved as <strong>auction_id</strong>
            and will appear in the same Invoice History table as
            Individual Invoice.
          </Typography>
        </Box>

        {/* =========================
            EXCEL UPLOAD
        ========================= */}

        <Box
          className={classes.dropZone}
          onClick={() =>
            inputRef.current &&
            inputRef.current.click()
          }
        >
          <CloudUploadIcon
            color="primary"
            style={{ fontSize: 48 }}
          />

          <Typography
            variant="h6"
            gutterBottom
          >
            Upload Excel File
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
          >
            Select an .xlsx or .xls file containing
            your invoice data.
          </Typography>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={(event) =>
              handleFile(
                event.target.files[0]
              )
            }
          />

          <Box mt={2}>
            <Button
              className={classes.primary}
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
              disabled={!selectedAuction}
            >
              Choose Excel File
            </Button>
          </Box>

          {file && (
            <Typography
              className={classes.fileName}
            >
              Selected: {file.name}
            </Typography>
          )}
        </Box>

        {error && (
          <Box mt={2}>
            <Typography color="error">
              {error}
            </Typography>
          </Box>
        )}

        <Box className={classes.actions}>
          <Button
            className={classes.secondary}
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={createTemplate}
          >
            Download Excel Template
          </Button>

          <Button
            className={classes.secondary}
            variant="outlined"
            onClick={onBack}
          >
            Back
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

BulkInvoiceUpload.defaultProps = {
  file: null,
  auctions: [],
  selectedAuction: null,
};

BulkInvoiceUpload.propTypes = {
  file: PropTypes.object,
  auctions: PropTypes.array,
  selectedAuction: PropTypes.object,
  onAuctionChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onParsed: PropTypes.func.isRequired,
};

export default BulkInvoiceUpload;
